// The hover-revealed handle that opens a minimised chat side panel.
//
// A full-height strip hugging one edge of the chat: a thin sheet that rests
// just outside the frame and slides into view by EDGE_STRIP_WIDTH when the
// pointer comes near or the button takes keyboard focus, carrying one icon and
// a tooltip for what the panel holds. The strip is ALWAYS in the tree with a
// stable accessible name, so "hidden" is a paint decision and never an
// availability one — keyboard users tab to it and tests query it by name.
//
// Pointer events are the fiddly part. The zone itself takes none: dropping a
// click target over the right edge of the messages column would eat the
// scrollbar and break wheel scrolling through that strip. Instead we listen
// for `pointermove`/`pointerleave` on the nearest positioned parent (the chat
// frame the zone is absolutely positioned in) and compute whether the pointer
// sits within EDGE_ZONE_WIDTH of this side's edge, at ANY y within the
// parent's rect — full height, top to bottom (Adam, 8 Sep). The pill itself
// stays interactive so it can always be clicked or tabbed to.

import React, { useEffect, useRef, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

/** Width of the edge zone, in px — Adam's "outer 30px on either side". */
export const EDGE_ZONE_WIDTH = 30
export const EDGE_FADE_MS = 120
/** How far the edge strip slides into view, in px. Adam asked for 15–20,
 *  then a little more once the artifact count sat on the icon, then 32 so a
 *  "99+" count fits inside the strip above it, and 48 on the 12 Sep call
 *  (#1347 item 18): "when you roll on it it's like I'm here". */
export const EDGE_STRIP_WIDTH = 48
/** Arrival, a touch slower than it was (12 Sep call, #1347 item 18). */
export const EDGE_SLIDE_MS = 320
/** Leaving is quicker than arriving, and on a plainer curve. */
export const EDGE_RETREAT_MS = 160
/** The glyph lands this long after the strip does — depth, not decoration. */
export const EDGE_GLYPH_DELAY_MS = 60
export const EDGE_PULSE_MS = 600
/** Expo-out: fast off the edge, a soft landing. No overshoot — premium restraint.
 *  Shared with the composer card's hover so every hover in the chat arrives
 *  and leaves on the same two curves. */
export const EDGE_REVEAL_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'
export const EDGE_RETREAT_EASING = 'cubic-bezier(0.4, 0, 0.6, 1)'
/** How far the glyph nudges toward the chat under the pointer: the way the
 *  panel will push when clicked. */
const EDGE_GLYPH_NUDGE_PX = 2
/** The resting handle: a half-disc on the edge, halfway up, with a chevron
 *  pointing into the chat. It is what tells a first-time reader there is
 *  something at the edge at all (Adam, 11 Sep); the strip above is what a
 *  pointer finds once it goes looking. */
export const EDGE_HANDLE_WIDTH = 18
export const EDGE_HANDLE_HEIGHT = 44

export interface EdgeRevealProps {
  /** Which edge of the chat this handle hugs. */
  side: 'left' | 'right'
  /** Accessible name and tooltip, e.g. "Open tables panel". */
  label: string
  /** Optional count on the pill. Absent or 0 renders no badge. */
  badge?: number
  onOpen: () => void
  /** True while the panel is open: the handle has nothing to offer, so it goes. */
  hidden?: boolean
  /**
   * One-shot attention pulse. Forces the pill visible and scales the badge, so
   * a first artifact arriving behind a closed panel is noticed without the
   * panel opening itself over whatever the user is reading.
   */
  pulse?: boolean
  /** What the panel holds, as an icon on the strip. Defaults to a chevron. */
  icon?: React.ReactNode
}

export default function EdgeReveal({ side, label, badge, onOpen, hidden, pulse, icon }: EdgeRevealProps) {
  const zoneRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  // Mirror of `hovered` for the pointer listeners, which are bound once.
  const hoveredRef = useRef(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The hand-off. When the panel opens over a strip that was in the tree, the
  // strip does not vanish: it stays for one retreat, riding the chat's edge as
  // the panel pushes it aside, sliding back under the panel — then leaves.
  // Decided in render (not an effect) so there is no null frame between the
  // panel opening and the retreat starting. A strip hidden from the start
  // has nothing to retreat from and renders nothing, as before.
  const [prevHidden, setPrevHidden] = useState(hidden)
  const [departing, setDeparting] = useState(false)
  if (hidden !== prevHidden) {
    setPrevHidden(hidden)
    setDeparting(Boolean(hidden))
  }
  useEffect(() => {
    if (!departing) return
    const t = setTimeout(() => setDeparting(false), EDGE_RETREAT_MS)
    return () => clearTimeout(t)
  }, [departing])

  useEffect(() => {
    const zone = zoneRef.current
    const parent = zone?.parentElement
    if (!parent) return

    const clearHideTimeout = () => {
      if (hideTimeoutRef.current !== null) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
    }

    // Hiding is debounced so a brief flick out of the band doesn't flicker
    // the pill; showing is immediate.
    const scheduleHide = () => {
      clearHideTimeout()
      hideTimeoutRef.current = setTimeout(() => {
        hoveredRef.current = false
        setHovered(false)
      }, EDGE_FADE_MS)
    }

    // The band that reveals the strip is EDGE_ZONE_WIDTH; once the strip is
    // out it is wider than that, and the pointer sitting on its inner part
    // must not count as having left — the strip would retreat from under
    // the pointer and come back the moment it moved. So while revealed the
    // band is the strip itself.
    const isWithinBand = (clientX: number, clientY: number) => {
      const rect = parent.getBoundingClientRect()
      const band = hoveredRef.current ? Math.max(EDGE_ZONE_WIDTH, EDGE_STRIP_WIDTH) : EDGE_ZONE_WIDTH
      const withinY = clientY >= rect.top && clientY <= rect.bottom
      const withinX =
        side === 'left'
          ? clientX >= rect.left && clientX <= rect.left + band
          : clientX <= rect.right && clientX >= rect.right - band
      return withinX && withinY
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (isWithinBand(e.clientX, e.clientY)) {
        clearHideTimeout()
        hoveredRef.current = true
        setHovered(true)
      } else {
        scheduleHide()
      }
    }

    const handlePointerLeave = () => {
      scheduleHide()
    }

    parent.addEventListener('pointermove', handlePointerMove)
    parent.addEventListener('pointerleave', handlePointerLeave)
    return () => {
      parent.removeEventListener('pointermove', handlePointerMove)
      parent.removeEventListener('pointerleave', handlePointerLeave)
      clearHideTimeout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `hidden` retriggers the effect so listeners detach while the panel is open.
  }, [side, hidden])

  if (hidden && !departing) return null

  // Chevron points INTO the chat: opening pushes the chat aside, it does not
  // pull the panel out over it. Hosts usually pass an icon that says what the
  // panel holds instead (tables, artifacts, history).
  const Chevron = side === 'left' ? ChevronRightIcon : ChevronLeftIcon
  const showBadge = typeof badge === 'number' && badge > 0
  const revealed = !hidden && (pulse || hovered)
  const glyph = icon ?? <Chevron fontSize="small" />
  const outward = `translateX(${side === 'left' ? '-' : ''}${EDGE_STRIP_WIDTH}px)`
  const inward = side === 'left' ? `translateX(${EDGE_GLYPH_NUDGE_PX}px)` : `translateX(-${EDGE_GLYPH_NUDGE_PX}px)`

  return (
    <Box
      ref={zoneRef}
      data-testid={`edge-reveal-${side}`}
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side]: 0,
        width: Math.max(EDGE_ZONE_WIDTH, EDGE_STRIP_WIDTH),
        zIndex: 2,
        // The zone is a hover hint, not a click target — see the header note.
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
      }}
    >
      {/* The resting handle. Decorative and clickable, not the accessible
          control — the strip button below carries the name — so it is out of
          the accessibility tree and the tab order. It fades as the strip
          arrives, and is gone while the panel is open or departing. */}
      {!departing && (
        <Box
          component="span"
          data-testid={`edge-reveal-handle-${side}`}
          aria-hidden
          onClick={onOpen}
          sx={{
            position: 'absolute',
            top: '50%',
            [side]: 0,
            transform: 'translateY(-50%)',
            width: EDGE_HANDLE_WIDTH,
            height: EDGE_HANDLE_HEIGHT,
            borderRadius: side === 'left' ? `0 ${EDGE_HANDLE_HEIGHT / 2}px ${EDGE_HANDLE_HEIGHT / 2}px 0` : `${EDGE_HANDLE_HEIGHT / 2}px 0 0 ${EDGE_HANDLE_HEIGHT / 2}px`,
            backgroundColor: 'background.paper',
            border: '1px solid rgba(38,55,87,0.14)',
            [side === 'left' ? 'borderLeft' : 'borderRight']: 'none',
            boxShadow: side === 'left' ? '2px 0 6px rgba(10,22,40,0.08)' : '-2px 0 6px rgba(10,22,40,0.08)',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            cursor: 'pointer',
            zIndex: 1,
            opacity: revealed ? 0 : 1,
            transition: `opacity ${EDGE_FADE_MS}ms ease`,
            '& svg': { fontSize: 16 },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <Chevron />
        </Box>
      )}
      {/* The whole edge slides in by EDGE_STRIP_WIDTH (Adam, 9 Sep): a
          full-height strip that rests just outside the frame and eases into
          view, carrying one icon for what the panel holds. Always in the
          tree with its accessible name; "hidden" is only a translate. */}
      <Tooltip title={label} placement={side === 'left' ? 'right' : 'left'}>
        <Box
          component="button"
          type="button"
          aria-label={label}
          onClick={onOpen}
          className="edge-reveal-strip"
          data-revealed={revealed ? 'true' : 'false'}
          data-departing={departing ? 'true' : undefined}
          // Departing: drawn, but no longer offered — not focusable, not in
          // the accessibility tree, and no click target under the panel.
          aria-hidden={departing ? true : undefined}
          tabIndex={departing ? -1 : undefined}
          style={{
            transform: revealed ? 'translateX(0)' : outward,
            // Arrive on the expo curve; leave quicker, on a plainer one.
            transition: `transform ${revealed ? EDGE_SLIDE_MS : EDGE_RETREAT_MS}ms ${revealed ? EDGE_REVEAL_EASING : EDGE_RETREAT_EASING}`,
            pointerEvents: departing ? 'none' : undefined,
          }}
          sx={{
            width: EDGE_STRIP_WIDTH,
            alignSelf: 'stretch',
            p: 0,
            m: 0,
            border: 'none',
            borderRight: side === 'left' ? '1px solid rgba(10,22,40,0.12)' : 'none',
            borderLeft: side === 'right' ? '1px solid rgba(10,22,40,0.12)' : 'none',
            pointerEvents: 'auto',
            cursor: 'pointer',
            // Revealed UNDER the page (Adam, 9 Sep): a recessed ground with the
            // page's edge casting its shadow inward onto the strip, and no
            // outward shadow, so the strip reads as beneath rather than on top.
            backgroundColor: '#eef2f7',
            boxShadow: side === 'left'
              ? 'inset -12px 0 14px -10px rgba(10,22,40,0.35)'
              : 'inset 12px 0 14px -10px rgba(10,22,40,0.35)',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `background-color 160ms ease`,
            '& svg': { fontSize: 18 },
            // The glyph (count + icon) is its own layer: it lands a beat after
            // the strip, nudges toward the chat under the pointer — the way
            // the panel will push when clicked — and presses on click.
            '& .edge-reveal-glyph': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.75,
              transition: `opacity 180ms ease, transform ${EDGE_SLIDE_MS}ms ${EDGE_REVEAL_EASING}`,
            },
            '&[data-revealed="false"] .edge-reveal-glyph': {
              opacity: 0,
              transform: `translateX(${side === 'left' ? '-' : ''}8px)`,
              transitionDelay: '0ms',
            },
            '&[data-revealed="true"] .edge-reveal-glyph': {
              opacity: 1,
              transform: 'translateX(0)',
              transitionDelay: `${EDGE_GLYPH_DELAY_MS}ms`,
            },
            '&:hover': { color: 'text.primary', backgroundColor: '#e7ecf3' },
            '&:hover .edge-reveal-glyph': { transform: inward },
            '&:active .edge-reveal-glyph': { transform: `${inward} scale(0.9)`, transitionDuration: '80ms' },
            '&:focus-visible': { transform: 'translateX(0) !important', outline: 'none' },
            '&:focus-visible .edge-reveal-glyph': { opacity: 1, transform: 'translateX(0)' },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none !important',
              '& .edge-reveal-glyph': { transition: 'none !important', transitionDelay: '0ms !important' },
            },
          }}
        >
          <Box component="span" className="edge-reveal-glyph">
          {/* The count sits ABOVE the icon as its own small pill, never over it
              (Adam, 9 Sep: the badge overlapped the icon). */}
          {showBadge && (
            <Box
              component="span"
              data-testid="edge-reveal-count"
              sx={{
                minWidth: 18,
                height: 16,
                px: '4px',
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 600,
                lineHeight: '16px',
                textAlign: 'center',
                color: '#fff',
                backgroundColor: 'info.main',
                '@keyframes edgeRevealBadgePulse': {
                  '0%': { transform: 'scale(1)' },
                  '45%': { transform: 'scale(1.45)' },
                  '100%': { transform: 'scale(1)' },
                },
                animation: pulse ? `edgeRevealBadgePulse ${EDGE_PULSE_MS}ms ease` : 'none',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none !important' },
              }}
            >
              {badge}
            </Box>
          )}
          {glyph}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  )
}
